import { Component, OnDestroy, OnInit, NgZone, HostListener, ElementRef, ViewChildren, QueryList } from '@angular/core';
import { Subscription, Subject } from 'rxjs';
import { Constants } from '../constants/constants';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { NgxPubSubService } from '@pscoped/ngx-pub-sub';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { Messages } from '../messages/messages';
import { takeUntil } from 'rxjs/operators';
import { UserInfoService } from '../services/user-info/user-info.service';
import { StorageProvider } from '../http/storage-service';
import { DialogService } from '@progress/kendo-angular-dialog';
import { Location } from '@angular/common';
import { UnAuthorizedAccessModalComponent } from 'src/app/shared/components/unauthorized-access-modal/unauthorized-access-modal.component';
import { CommonUtilsService } from '../services/utils/common-utils.service';
import { SessionConfirmModalComponent } from 'src/app/shared/components/confirm-session-modal/session-confirm-modal.component';

declare var window: any;
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  dialogRefConnectionTimeout: any;
  private onDestroy$: Subject<void> = new Subject<void>();

  private subscriptionUnauthorized: Subscription;
  private subscriptionMsalLoginSuccess: Subscription;
  private subscriptionConnectionTimeoutError: Subscription;
  private subscriptionLlwProcessInProgress: Subscription;

  // --- Subscriptions variables for idle/KeepAlive ------//
  private subscriptionIdleStart: Subscription;
  private subscriptionIdleEnd: Subscription;
  private subscriptionIdleTimeout: Subscription;
  private subscriptionKeepAlive: Subscription;

  idleState = 'Not started.';
  lastPing?: Date = null;
  idleTimeout: number = Constants.IdleTimeout;
  forceTimeout: number = Constants.ForceTimeout;
  accessMenus: any = [];
  userData: any;
  splitedUserName: any;
  showLinksBox = false;
  isLoading = false;
  dbServer: any;
  userResponsibility: any;
  isProdEnv = false;
  @ViewChildren('menuEl') menuComponent: QueryList<ElementRef>;
  constructor(private router: Router, private msalService: MsalService, private storageProvider: StorageProvider,
              private eventService: NgxPubSubService, private ngZone: NgZone, private eRef: ElementRef,
              private idle: Idle, private keepAlive: Keepalive, private objUserInfoService: UserInfoService,
              private dialogService: DialogService, private location: Location, private objCommonUtilsProvider: CommonUtilsService) {
  }

  /**
   * Desc: Method called on page initialization.
   */
  ngOnInit() {
    this.subscriptionUnauthorized = this.eventService.subscribe
      (Constants.UnauthorizedAccessEvent, data => this.showUnauthorizedDialog());

    this.subscriptionMsalLoginSuccess = this.eventService.subscribe
      (Constants.MsalLoginSuccessEvent, data => {
        if (!data) {
          this.isLoading = true;
        }
        this.getLoginUserData(data);
        this.startIdleWatchSession();
      });

    this.subscriptionConnectionTimeoutError = this.eventService.subscribe
      (Constants.ConnectionTimeoutErrorEvent, data => this.showDialog(Messages.Dialog_Timeout_Message_Error));

    this.subscriptionLlwProcessInProgress = this.eventService.subscribe
      (Constants.InitiateLlwInProgressEvent, data => this.keepSessionAlive(data));
  }

  /**
   * Desc:Method called on page destroy.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    if (this.subscriptionUnauthorized) {
      this.subscriptionUnauthorized.unsubscribe();
    }
    if (this.subscriptionMsalLoginSuccess) {
      this.subscriptionMsalLoginSuccess.unsubscribe();
    }

    if (this.subscriptionConnectionTimeoutError) {
      this.subscriptionConnectionTimeoutError.unsubscribe();
    }
    if (this.subscriptionLlwProcessInProgress) {
      this.subscriptionLlwProcessInProgress.unsubscribe();
    }
    if (this.subscriptionIdleTimeout) {
      this.subscriptionIdleTimeout.unsubscribe();
    }
    if (this.subscriptionIdleStart) {
      this.subscriptionIdleStart.unsubscribe();
    }
    if (this.subscriptionIdleEnd) {
      this.subscriptionIdleEnd.unsubscribe();
    }
  }

  /**
   * Function to fetch login user information
   * @param isLoggedIn Is login boolean value
   */
  getLoginUserData(isLoggedIn) {
    this.objUserInfoService.getMenuMapping()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (retData: any) => {
          this.isLoading = false;
          if (retData.message !== null && retData.message !== '') {
            this.showUnauthorizedDialog(retData.message);
          }
          if (retData.data !== null) {
            this.bindMenu(retData.data, isLoggedIn);
          }
        }, (error) => {
          this.showLogoutConfirmationDialog(Messages.Dialog_Authentication_Error);
        }
      );
  }

  /**
   * Function called to bind user information
   * @param data user's data
   * @param isLoggedIn is login boolean value
   */
  bindMenu(data, isLoggedIn) {
    const prodEnvName = 'prod';
    this.userData = data.user;
    // const userNameArr = data.user.userName.split(' ');
    // this.splitedUserName = userNameArr[1] + ', ' + userNameArr[0];
    if (data.appDetails != null) {
      this.dbServer = data.appDetails.dbServer;
      if (data.appDetails.envName.toLowerCase() === prodEnvName) {  // check if Env is PROD //
        this.isProdEnv = true;
      } else {
        this.isProdEnv = false;
      }
    }

    // bind user responsibility //
    if (data.responsibility !== null && data.responsibility !== undefined && data.responsibility.length > 0) {
      this.userResponsibility = data.responsibility[0].responsibilityName;
    } else {
      this.userResponsibility = '';
    }

    this.accessMenus = data.accessMenus;

    this.objUserInfoService.setLoggedInUserDetails(data);
    const path = this.location.path();
    if (!isLoggedIn || path === '') {
      this.navigateLandingScreen();
    }
  }

  /**
   * Function will navigate to landing screen
   */
  navigateLandingScreen() {
    const landingScreen = '/project/project-search';
    this.storageProvider.setSessionStorageData(Constants.IsUserLoggedIn, true);  // already logged in
    this.router.navigate([landingScreen]);
  }

  /**
   * Desc: Function to show unAuthorized user access dialog.
   */
  showUnauthorizedDialog(msg?: any) {
    const dialogRef = this.dialogService.open({
      content: UnAuthorizedAccessModalComponent,
    });
    const userInfo = dialogRef.content.instance;
    userInfo.message = Messages.UnAuthorizedAccess;
    if (msg) {
      userInfo.message = msg;
    }
    // userInfo.headerTitle = Messages.SessionTimeoutTitle;
    userInfo.buttonTitle = Messages.SignOutButton;
    dialogRef.result.subscribe((resp: any) => {
      if (resp.data) {
        this.logoutUserSessionExpire();
      }
    });
  }

  /**
   * Desc: Function to show connection timeout dialog.
   */
  showDialog(msg: string) {
    if (!this.dialogRefConnectionTimeout) {
      this.dialogRefConnectionTimeout = this.dialogService.open({
        content: UnAuthorizedAccessModalComponent,
      });
      const userInfo = this.dialogRefConnectionTimeout.content.instance;
      userInfo.message = msg;
      this.dialogRefConnectionTimeout.result
        .subscribe(resp => {
          this.dialogRefConnectionTimeout = null;  // clear old reference
        });
    }
  }

  /**
   * Descp:Function to start idle/keepAlive session
   */
  startIdleWatchSession() {
    if (this.idle.isRunning()) {
      this.endLocalSession();
      this.startLocalSession();   // start local session
    } else {
      this.startLocalSession();   // start local session
    }
  }

  /**
   * Desc: Function to start active local session time.
   */
  startLocalSession() {
    this.idle.setIdle(this.idleTimeout);
    this.idle.setTimeout(this.forceTimeout);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.subscriptionIdleTimeout = this.idle.onTimeout.subscribe(() => {
      this.idleState = 'TIMED_OUT';
      this.logoutUserSessionExpire();
    });

    this.subscriptionIdleStart = this.idle.onIdleStart.subscribe(() => {
      this.idleState = 'IDLE_START';
      this.showSessionAlert();
    });

    this.subscriptionIdleEnd = this.idle.onIdleEnd.subscribe(() => {
      this.idleState = 'NO_LONGER_IDLE';
    });

    this.subscriptionKeepAlive = this.keepAlive.onPing.subscribe(() => {
      this.lastPing = new Date();
      console.log('last ping is ' + this.lastPing);
    });


    this.idle.watch();
    this.idleState = 'Started.';
  }

  /**
   * Method to keep session alive
   * @param eventData:event data from publisher
   */
  keepSessionAlive(eventData) {
    console.log('+++ interrupt event-keep session active');
    if (eventData) {
      this.keepAlive.ping();
      this.idle.watch();
    }
  }

  /**
   * Descp: Function to end active local session time.
   */
  endLocalSession() {
    this.idle.stop();
    this.idleState = 'Not started.';
    if (this.subscriptionIdleTimeout) {
      this.subscriptionIdleTimeout.unsubscribe();
    }
    if (this.subscriptionIdleStart) {
      this.subscriptionIdleStart.unsubscribe();
    }
    if (this.subscriptionIdleEnd) {
      this.subscriptionIdleEnd.unsubscribe();
    }
    if (this.subscriptionKeepAlive) {
      this.subscriptionKeepAlive.unsubscribe();
    }
  }

  /**
   * Descp: Function to show session alert dialog.
   */
  showSessionAlert(msg?: any) {
    this.ngZone.run(() => {
      const dialogRef = this.dialogService.open({
        content: SessionConfirmModalComponent,
      });
      const userInfo = dialogRef.content.instance;
      userInfo.message = Messages.Session_Timeout_Warning;
      if (msg) {
        userInfo.message = msg;
      }
      dialogRef.result.subscribe((resp: any) => {
        if (!resp['data']) {
          this.logoutUserSessionExpire();
        }
      });
    });
  }

  /**
   * Descp:Function to clear session storage data.
   */
  clearStorageData() {
    this.objUserInfoService.removeStorageData(Constants.IsUserLoggedIn);
    this.objUserInfoService.removeStorageData(Constants.LoggedInUserData);
    this.objUserInfoService.removeStorageData(Constants.UserPrefGridPageSize);
    this.objCommonUtilsProvider.setSearchProjectCriteria(null);
    this.objCommonUtilsProvider.setSearchedProjectData(null);
    this.objCommonUtilsProvider.setGlobalGridPageSize(null);
  }

  /**
   * Descp: Method to perform log out user session.
   */
  logoutUserSessionExpire() {
    this.endLocalSession();
    this.clearStorageData();
    this.msalService.logout();
  }

  /**
   * Desc: Navigation for sub menu.
   * @param menuUrl sub menu url
   */
  menuNavigation(menuUrl) {
    const projectRouterPath = '/project/create-project';
    const path = this.location.path();
    const projectScreenUrl = 'project/create-project';
    if (path === projectRouterPath && menuUrl === projectScreenUrl) {
      this.eventService.publishEvent(Constants.AccessProjectFromMenuEvent, true);
    } else {
      this.objCommonUtilsProvider.setSearchProjectCriteria(null);
      this.router.navigate([menuUrl]);
    }
  }

  /**
   * Description: Close More button by clicking anywhere on the screen.
   */
  @HostListener('document:click', ['$event'])
  clickout(event) {
    if (!(this.eRef.nativeElement.contains(event.target))) {
      this.showLinksBox = false;
    }
  }

  /**
   * Desc: Function to open log out menu.
   */
  public openLogoutMenu() {
    if (this.showLinksBox === false) {
      this.showLinksBox = true;
    } else {
      this.showLinksBox = false;
    }
  }

  /**
   * Desc: Function is called , when log out button is clicked from logout Menu
   */
  public appLogout() {
    this.logoutUserSessionExpire();
  }

  activateClass() {
    const allListElements = this.menuComponent.toArray();
    allListElements.forEach((val) => {
      if (val.nativeElement.className === 'main-link highlighted') {
        val.nativeElement.className = 'main-link';
      }
    });
    const foundElement: any = allListElements.find((x: any) => x.nativeElement.classList);
    const myTag = (<HTMLElement> (<HTMLElement> event.target).parentNode.parentNode.parentNode);
    myTag.classList.add('highlighted');


  }

  /**
   * Function to logout
   */
  appLogoutConfirm() {
    this.showLogoutConfirmationDialog(Messages.Logout_Confirmation);
  }

  /**
   * Descp: Function to show session alert dialog.
   * @param titleMessage:message to show in title
   */
  showLogoutConfirmationDialog(logoutTitleMessage) {
    const dialogTitle = 'Confirmation';
    const dialogRef = this.dialogService.open({
      content: SessionConfirmModalComponent,
    });
    const userInfo = dialogRef.content.instance;
    userInfo.message = logoutTitleMessage;
    userInfo.headerTitle = dialogTitle;
    userInfo.buttonTitleFirst = Messages.Dialog_Button_Ok;
    userInfo.buttonTitleSecond = Messages.Logout_Dialog_Button_Cancel;
    dialogRef.result.subscribe(resp => {
      if (resp['data']) {
        this.appLogout();
      }
    });
  }
}
